import {
  Box,
  Button,
  Divider,
  HStack,
  Image,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Menu, Props } from "./types";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IconChevronDown } from "@/components/icons/chevronDown";

const logoImage = "/images/company-logo.png";

// ─── Extrai eventId da URL ────────────────────────────────────────────────────
const extractEventId = (pathname: string): string => {
  const matchInline = pathname.match(/\/eventos\/(\d+)\//);
  if (matchInline) return matchInline[1];
  const matchEnd = pathname.match(/\/eventos\/[^/]+\/(\d+)(?:\/|$)/);
  if (matchEnd) return matchEnd[1];
  return "";
};

const buildEventTabs = (eventId: string) => [
  {
    key: "formandos",

    label: "Clientes",
    path: `/eventos/${eventId}/clientes`,
  },
  {
    key: "usuarios-portaria",

    label: "Usuário Portaria",
    path: `/eventos/usuarios-portaria/${eventId}`,
  },
  {
    key: "relatorio-retirada",

    label: "Relatório de Retirada",
    path: `/eventos/relatorio-retirada/${eventId}`,
  },
  {
    key: "relatorio-acesso",

    label: "Relatório de Acesso",
    path: `/eventos/relatorio-acesso/${eventId}`,
  },
];

export const SideBar = ({ menus }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuSelected, setMenuSelected] = useState<number | undefined>();

  const eventId = extractEventId(location.pathname);
  const isOnEventPage = !!eventId;
  const eventTabs = isOnEventPage ? buildEventTabs(eventId) : [];

  const handleClick = useCallback(
    (menu: Menu) => () => {
      if (menu.route) navigate(menu.route);
      if (menu.id === menuSelected) {
        setMenuSelected(undefined);
        return;
      }
      setMenuSelected(menu.id);
    },
    [menuSelected]
  );

  const isSubMenuActive = (route: string) =>
    location.pathname.startsWith(route);

  return (
    <Box
      w="260px"
      flexShrink={0}
      minH="100vh"
      h="100%"
      bg="neutral.500"
      borderRight="1px solid"
      borderRightColor="whiteAlpha.80"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* ── Logo ── */}
      <Stack alignItems="center" py={6} px={5} flexShrink={0}>
        <Image
          w="180px"
          src={logoImage}
          alt="Logo"
          opacity={0.95}
          _hover={{ opacity: 1 }}
          transition="opacity 0.2s"
        />
      </Stack>

      <Divider borderColor="whiteAlpha.80" />

      {/* ── Menus principais ── */}
      <VStack
        spacing={0}
        flex={1}
        overflowY="auto"
        pt={4}
        pb={4}
        alignItems="stretch"
        sx={{
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            background: "whiteAlpha.200",
            borderRadius: "3px",
          },
        }}
      >
        {menus.map((menu) => {
          const isOpen = menuSelected === menu.id;
          const hasSubMenus = !!menu.subMenus?.length;
          const isActive =
            menu.route && location.pathname.startsWith(menu.route);

          return (
            <Box key={menu.id}>
              <Button
                w="100%"
                h="44px"
                variant="ghost"
                borderRadius={0}
                px={5}
                justifyContent="flex-start"
                position="relative"
                color={isActive ? "primary.500" : "neutral.200"}
                fontFamily="Inter"
                fontWeight={isActive ? "600" : "500"}
                fontSize="13px"
                letterSpacing="0.01em"
                bg={isActive ? "whiteAlpha.60" : "transparent"}
                _hover={{ bg: "whiteAlpha.80", color: "neutral.0" }}
                _active={{ bg: "whiteAlpha.150" }}
                onClick={handleClick(menu)}
                _before={
                  isActive
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: "8px",
                        bottom: "8px",
                        w: "2px",
                        bg: "primary.500",
                        borderRadius: "0 2px 2px 0",
                      }
                    : {}
                }
              >
                <HStack w="100%" justifyContent="space-between" spacing={2}>
                  <Text>{menu.name}</Text>
                  {hasSubMenus && (
                    <Box
                      color="neutral.400"
                      transform={isOpen ? "rotate(0deg)" : "rotate(-90deg)"}
                      transition="transform 0.2s ease"
                      flexShrink={0}
                    >
                      <IconChevronDown />
                    </Box>
                  )}
                </HStack>
              </Button>

              {isOpen && hasSubMenus && (
                <Box
                  bg="neutral.550"
                  borderTop="1px solid"
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.80"
                >
                  {menu.subMenus!.map((subMenu) => {
                    const subActive = isSubMenuActive(subMenu.route);
                    return (
                      <Button
                        key={subMenu.id}
                        w="100%"
                        h="38px"
                        variant="ghost"
                        borderRadius={0}
                        pl="32px"
                        pr={5}
                        justifyContent="flex-start"
                        color={subActive ? "primary.500" : "neutral.300"}
                        fontFamily="Inter"
                        fontWeight={subActive ? "600" : "400"}
                        fontSize="12px"
                        letterSpacing="0.01em"
                        bg={subActive ? "whiteAlpha.60" : "transparent"}
                        _hover={{ bg: "whiteAlpha.80", color: "neutral.0" }}
                        _active={{ bg: "whiteAlpha.150" }}
                        onClick={() => navigate(subMenu.route)}
                        position="relative"
                        _before={
                          subActive
                            ? {
                                content: '""',
                                position: "absolute",
                                left: 0,
                                top: "8px",
                                bottom: "8px",
                                w: "2px",
                                bg: "primary.500",
                                borderRadius: "0 2px 2px 0",
                              }
                            : {}
                        }
                      >
                        <HStack spacing={3}>
                          <Box
                            w="4px"
                            h="4px"
                            borderRadius="full"
                            bg={subActive ? "primary.500" : "neutral.400"}
                            flexShrink={0}
                          />
                          <Text>{subMenu.name}</Text>
                        </HStack>
                      </Button>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}

        {/* ── Tabs do Evento (aparece só quando está em página de evento) ── */}
        {isOnEventPage && (
          <>
            <Divider borderColor="whiteAlpha.80" mx={5} w="auto" mt={2} />
            <Box px={5} pt={4} pb={1}>
              <Text
                fontFamily="Inter"
                fontSize="9px"
                fontWeight="700"
                color="whiteAlpha.500"
                letterSpacing="0.1em"
                textTransform="uppercase"
              >
                Navegação do Evento
              </Text>
            </Box>
            {eventTabs.map((tab) => {
              const isActive = location.pathname.includes(tab.key);
              return (
                <Button
                  key={tab.key}
                  w="100%"
                  h="40px"
                  variant="ghost"
                  borderRadius={0}
                  pl="24px"
                  pr={5}
                  justifyContent="flex-start"
                  position="relative"
                  color={isActive ? "primary.500" : "neutral.300"}
                  fontFamily="Inter"
                  fontWeight={isActive ? "600" : "400"}
                  fontSize="12px"
                  letterSpacing="0.01em"
                  bg={isActive ? "whiteAlpha.60" : "transparent"}
                  _hover={{ bg: "whiteAlpha.80", color: "neutral.0" }}
                  _active={{ bg: "whiteAlpha.150" }}
                  onClick={() => navigate(tab.path)}
                  _before={
                    isActive
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "8px",
                          bottom: "8px",
                          w: "2px",
                          bg: "primary.500",
                          borderRadius: "0 2px 2px 0",
                        }
                      : {}
                  }
                >
                  <HStack spacing={3}>
                    <Box
                      w="4px"
                      h="4px"
                      borderRadius="full"
                      bg={isActive ? "primary.500" : "neutral.400"}
                      flexShrink={0}
                    />
                    <Text>{tab.label}</Text>
                  </HStack>
                </Button>
              );
            })}
          </>
        )}
      </VStack>

      {/* ── Rodapé ── */}
      <Divider borderColor="whiteAlpha.80" />
      <Box px={5} py={3} flexShrink={0}>
        <Text
          fontFamily="Inter"
          fontSize="10px"
          fontWeight="400"
          color="neutral.500"
          letterSpacing="0.05em"
          textTransform="uppercase"
        >
          v1.0.0
        </Text>
      </Box>
    </Box>
  );
};
